import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { NoteInstruction, InstructionTypes } from '../../../../types/instructions';
import { selectNoteInstruction } from '../../../../store/modules/script-editor/selectors';
import { setOpenNoteInstructionModal } from '../../../../store/modules/script-editor/actions';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import { emitInstructionEventByEditor, removeInstructionElement, setInstructionActionTooltip } from '../utils';
import { InstructionActionTooltip, InstructionEvent } from '../types';
import {
  ModalComponent,
  InputComponent,
} from '../components';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';
const defaultState = {
  comment: '',
  url: '',
};

const NoteInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const { getValues, setValue } = useFormContext();
  const { isOpenModal, instructionId, isInline } = useSelector(selectNoteInstruction);
  const [comment, setComment] = useState(defaultState.comment);
  const [url, setUrl] = useState(defaultState.url);
  const [initialState, setInitialState] = useState(defaultState);

  useEffect(() => {
    (async () => {
      if (instructionId) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.noteInstruction,
        );
        if (instruction) {
          const { data } = instruction as NoteInstruction;
          setComment(instruction.comment);
          setUrl(data.url);
          setInitialState({
            comment: instruction.comment,
            url: data.url,
          });
        } else {
          setComment(defaultState.comment);
          setUrl(defaultState.url);
          setInitialState(defaultState);
        }
      } else {
        setComment(defaultState.comment);
        setUrl(defaultState.url);
        setInitialState(defaultState);
      }
    })();
  }, [
    setComment,
    setUrl,
    instructionId,
    getValues,
    setInitialState,
  ]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_NOTE_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenNoteInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getSummary = useCallback(() => {
    if (comment.trim()) {
      return comment;
    }
    if (url.trim()) {
      return url;
    }
    return 'Note';
  }, [comment, url]);

  const saveHandler = useCallback(() => {
    (async () => {
      if (instanceEditor) {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.noteInstruction,
        );
        const noteInstruction: NoteInstruction = {
          id: instructionId,
          type: InstructionTypes.noteInstruction,
          comment,
          needsInfo: false,
          data: {
            url,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = noteInstruction;
          if (isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.note}: ${summary}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_NOTE_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            )
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          noteInstruction.id = id;
          scriptData.instructions.push(noteInstruction);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_NOTE_INSTRUCTION,
              { instructionId: id, tooltip: summary },
            );
          }
        }
        if (!isInline) {
          let textData = comment;
          const link = url.trim();
          if (link) {
            textData += ` <a href="${link}" target="_blank">${link}</a>`;
          }
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_NOTE_INSTRUCTION_BLOCK,
            { instructionId: noteInstruction.id, text: textData || 'Editor Note', tooltip: summary },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      }
    })();
  }, [
    instanceEditor,
    getValues,
    setValue,
    instructionId,
    closeHandler,
    comment,
    url,
    isInline,
    getSummary,
  ]);

  const cancelHandler = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setUrl(initialState.url);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
      ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    closeHandler,
    setComment,
    setUrl,
    initialState,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveHandler}
      cancelHandler={cancelHandler}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <InputComponent
            label="URL"
            value={url}
            onChange={setUrl}
          />
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default NoteInstructionModal;
